// ─────────────────────────────────────────────────────────────────────────────
// dp-collection-filters.js
// Alles rund um die Filter auf Collection-Seiten:
//   • Datumsfilter als Kalender (nutzt DpCalendar aus dp-calendar.js)
//   • Active-Facet-Pillen: Timestamp → lesbares Datum
//   • Stadt-Filter mit Suchfeld
//   • Ajaxinate (Endlos-Laden)
// ─────────────────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  // ─── 1. Konfiguration ────────────────────────────────────────────────────
  // Alle Filter, die als Kalender dargestellt werden sollen.
  // key        = Parametername aus Shopify (steht auch in der Dialog-ID)
  // pillPrefix = Präfix für die "Active Facet"-Pille, damit man bei gleichem
  //              Datum unterscheiden kann, welcher Filter aktiv ist.
  // Weitere Datumsfilter einfach hier ergänzen (+ Selektor in dp-custom.css).
  const DATE_FILTERS = [
    { key: 'filter.p.m.custom.date_filter',          pillPrefix: '' },
    { key: 'filter.v.m.custom.datumfilter_workshop', pillPrefix: 'Workshop: ' }
  ];
  const DATE_FILTER_KEYS = DATE_FILTERS.map(f => f.key);
  const STADT_DIALOG_ID = 'filter-dialog-filter.p.m.custom.stadt';

  function getFilterConfig(key) {
    return DATE_FILTERS.find(f => f.key === key) || { key, pillPrefix: '' };
  }

  // Wartet auf dp-calendar.js, egal in welcher Reihenfolge eingebunden
  function whenCalendar(cb) {
    if (window.DpCalendar) cb(window.DpCalendar);
    else document.addEventListener('dp:calendar-ready', () => cb(window.DpCalendar), { once: true });
  }

  // Pillen-Label "Do., 22. Oktober"
  const _labelFmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', weekday: 'short', day: 'numeric', month: 'long'
  });
  function tsToLabel(ts) {
    return _labelFmt.format(new Date(ts * 1000));
  }

  // Eigener Key-Cache, damit die Pillen auch ohne dp-calendar.js funktionieren
  const _keyFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const _keyCache = new Map();
  function tsToKey(ts) {
    let key = _keyCache.get(ts);
    if (key === undefined) {
      key = _keyFmt.format(new Date(ts * 1000));
      _keyCache.set(ts, key);
    }
    return key;
  }

  function getSelectedDaysFromURL(filterKey) {
    const params = new URLSearchParams(window.location.search);
    const selected = new Set();
    params.getAll(filterKey).forEach(ts => {
      const n = parseInt(ts, 10);
      if (!isNaN(n)) selected.add(tsToKey(n));
    });
    return selected;
  }


  // ─── 2. Active Facets patchen ────────────────────────────────────────────
  // Struktur: <div class="removable-facet">TIMESTAMP<a href="...remove-url...">x</a></div>
  // Ziel: Timestamp → Datum-Label, Duplikate pro Tag + Filter ausblenden,
  //       Entfernen-Link des sichtbaren Pills entfernt ALLE Timestamps
  //       desselben Tages aus DEMSELBEN Filter.

  // Ermittelt, zu welchem Datumsfilter eine Pille gehört. Derselbe Timestamp
  // kann in mehreren Filtern vorkommen (z.B. 1795806000) – dann entscheidet
  // der Entfernen-Link: Im Link fehlt der Wert genau in seinem Filter.
  function findFilterKeyForFacet(el, tsStr) {
    const current = new URLSearchParams(window.location.search);
    const candidates = DATE_FILTER_KEYS.filter(k => current.getAll(k).includes(tsStr));
    if (candidates.length <= 1) return candidates[0] || null;

    const link = el.querySelector('a');
    if (!link) return candidates[0];
    const linkParams = new URL(link.getAttribute('href'), window.location.origin).searchParams;
    const count = (p, k) => p.getAll(k).filter(v => v === tsStr).length;
    return candidates.find(k => count(linkParams, k) < count(current, k)) || candidates[0];
  }

  function patchActiveFacets() {
    const container = document.querySelector('.active-facets');
    if (!container) return;

    // Nur noch nicht verarbeitete Pillen, deren Textknoten ein Unix-Timestamp ist.
    const dateFacets = [...container.querySelectorAll('.removable-facet')].filter(el => {
      if (el.dataset.dpDone) return false;
      const text = el.childNodes[0]?.textContent?.trim();
      return text && /^\d{9,11}$/.test(text);
    });
    if (!dateFacets.length) return;

    // Erst ALLE Zuordnungen bestimmen, bevor irgendein Link verändert wird.
    const groups = {}; // `${filterKey}|${dateKey}` → [{ el, ts, filterKey }]
    dateFacets.forEach(el => {
      const tsStr = el.childNodes[0].textContent.trim();
      const filterKey = findFilterKeyForFacet(el, tsStr);
      el.dataset.dpDone = '1';
      if (!filterKey) return; // kein Datumsfilter aus unserer Liste
      const ts = parseInt(tsStr, 10);
      const groupKey = `${filterKey}|${tsToKey(ts)}`;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push({ el, ts, filterKey });
    });

    Object.values(groups).forEach(items => {
      const first = items[0];
      const { pillPrefix } = getFilterConfig(first.filterKey);
      const label = pillPrefix + tsToLabel(first.ts);

      first.el.childNodes[0].textContent = label;

      const link = first.el.querySelector('a');
      if (link) {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        const params = url.searchParams;

        // Alle Timestamps dieser Gruppe aus genau diesem Filter entfernen
        const remove = new Set(items.map(({ ts }) => String(ts)));
        const keep = params.getAll(first.filterKey).filter(v => !remove.has(v));
        params.delete(first.filterKey);
        keep.forEach(v => params.append(first.filterKey, v));

        link.setAttribute('href', url.pathname + (params.toString() ? '?' + params.toString() : ''));
        link.setAttribute('aria-label', `Filter "${label}" entfernen`);
      }

      items.slice(1).forEach(({ el }) => {
        el.style.display = 'none';
      });
    });
  }


  // ─── 3. Datumsfilter als Kalender ────────────────────────────────────────
  // Ablauf: Tage antippen (mehrere möglich, erneuter Klick hebt auf) →
  // "Filtern" → erst dann werden die versteckten Shopify-Checkboxen gesetzt
  // und das Theme lädt die Ergebnisse (ein einziger AJAX-Request).
  // Schließt man den Dialog ohne "Filtern", wird die Auswahl verworfen.

  // Angezeigter Monat pro Filter merken (überlebt AJAX-Neuaufbau des Dialogs)
  const _savedView = {}; // filterKey → 'YYYY-MM-01'

  class FacetDateCalendar {
    constructor(dialog, filterKey, DpCalendar) {
      this.dialog = dialog;
      this.filterKey = filterKey;
      this.DpCalendar = DpCalendar;
      this.eventMap = {};
      this.selectedDays = new Set();
      this._sig = null;
      this._init();
    }

    _init() {
      this.checkboxes = [...this.dialog.querySelectorAll('input[type="checkbox"]')];
      if (!this.checkboxes.length) return;

      this._buildEventMap();
      this.selectedDays = getSelectedDaysFromURL(this.filterKey);
      this._hideCheckboxes();

      const host = document.createElement('div');
      host.className = 'dp-facet-calendar';
      this.dialog.appendChild(host);

      this.cal = new this.DpCalendar(host, {
        showBars: false,
        resetLabel: 'Auswahl zurücksetzen',
        applyLabel: 'Filtern',
        view: this._startView(),
        getDay: (key) => this._getDay(key),
        onSelect: (key) => this._toggle(key),
        onReset: () => this._reset(),
        onApply: () => this._apply(),
        onViewChange: ({ year, month }) => {
          _savedView[this.filterKey] = this.DpCalendar.util.toKey(year, month, 1);
        }
      });
      this._updateBounds();
      this._render();

      // Dialog ohne "Filtern" geschlossen → nicht übernommene Auswahl verwerfen
      if (!this._openObserver) {
        this._openObserver = new MutationObserver(() => {
          if (!this.dialog.hasAttribute('open')) this._discard();
        });
        this._openObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      }

      this._sig = this._signature(this.checkboxes);
    }

    // Signatur aus verfügbaren Checkboxen + aktueller URL-Auswahl.
    // Ändert sich nichts, bricht refresh() sofort ab → kein DOM-Write.
    _signature(boxes) {
      const available = boxes.filter(cb => !cb.disabled).map(cb => cb.value).join(',');
      const selected = [...getSelectedDaysFromURL(this.filterKey)].sort().join(',');
      return available + '|' + selected;
    }

    refresh() {
      const boxes = [...this.dialog.querySelectorAll('input[type="checkbox"]')];
      if (!boxes.length) return;

      if (!this.cal) {
        this._init();
        return;
      }

      const sig = this._signature(boxes);
      if (sig === this._sig) return;
      this._sig = sig;
      this._applying = false; // neue Ergebnisse sind da

      this.checkboxes = boxes;
      this._buildEventMap();
      this._hideCheckboxes();
      this.selectedDays = getSelectedDaysFromURL(this.filterKey);
      this._updateBounds();
      this._ensureVisibleMonth();
      this._render();
    }

    _buildEventMap() {
      this.eventMap = {};
      const today = _keyFmt.format(new Date());
      this.checkboxes.forEach(cb => {
        // Deaktivierte Checkboxen (0 Treffer) sind nicht wählbar
        if (cb.disabled) return;
        const ts = parseInt(cb.value, 10);
        if (isNaN(ts)) return;
        const key = tsToKey(ts);
        // Vergangene Termine nicht anbieten: Shopify würde sonst die alte
        // (ausverkaufte) Variante auswählen und die Karte als "Ausverkauft" zeigen
        if (key < today) return;
        if (!this.eventMap[key]) this.eventMap[key] = [];
        this.eventMap[key].push(cb);
      });
    }

    _getDay(key) {
      const hasEvent = !!this.eventMap[key];
      const selected = this.selectedDays.has(key);
      // Gewählte Tage ohne Treffer bleiben klickbar, damit man sie abwählen kann
      if (!hasEvent && !selected) return null;
      return { selectable: true, selected };
    }

    _allKeys() {
      return [...new Set([...Object.keys(this.eventMap), ...this.selectedDays])].sort();
    }

    _startView() {
      const saved = _savedView[this.filterKey];
      if (saved) return saved;
      const selected = [...this.selectedDays].sort();
      return selected[0] || Object.keys(this.eventMap).sort()[0] || this.DpCalendar.util.todayKey();
    }

    _updateBounds() {
      const keys = this._allKeys();
      this.cal.setBounds(
        keys.length ? keys[0].slice(0, 7) : null,
        keys.length ? keys[keys.length - 1].slice(0, 7) : null
      );
    }

    // Springt auf den ersten Monat mit Terminen, falls der angezeigte Monat
    // nach einem Filterwechsel leer ist.
    _ensureVisibleMonth() {
      const keys = this._allKeys();
      if (!keys.length) return;
      const prefix = this.cal.viewMonth();
      if (keys.some(k => k.startsWith(prefix))) return;
      this.cal.setView(keys[0]);
    }

    _hideCheckboxes() {
      const wrapper = this.dialog.querySelector('.h-stack');
      if (wrapper) wrapper.style.display = 'none';
    }

    // Auswahl nur vormerken – gefiltert wird erst über den Button
    _toggle(key) {
      if (this.selectedDays.has(key)) this.selectedDays.delete(key);
      else this.selectedDays.add(key);
      // Erst nach dem Klick neu zeichnen: Würde der geklickte Tag sofort
      // ersetzt, hielte Impact den Klick für "außerhalb" und schlösse den Dialog.
      requestAnimationFrame(() => this._render());
    }

    _reset() {
      this.selectedDays.clear();
      this._render();
    }

    _apply() {
      if (!this._isDirty()) return;
      this._applying = true; // Schließen jetzt nicht als "verwerfen" werten
      this._syncAndSubmit();
      this.dialog.removeAttribute('open');
    }

    // Vorgemerkte Auswahl verwerfen → zurück auf den Stand aus der URL
    _discard() {
      if (this._applying || !this._isDirty()) return;
      this.selectedDays = getSelectedDaysFromURL(this.filterKey);
      this._render();
    }

    // Weicht die vorgemerkte Auswahl vom aktiven Filter (URL) ab?
    _isDirty() {
      const applied = getSelectedDaysFromURL(this.filterKey);
      if (applied.size !== this.selectedDays.size) return true;
      return [...this.selectedDays].some(k => !applied.has(k));
    }

    _render() {
      this.cal.render();

      const n = this.selectedDays.size;
      const dirty = this._isDirty();
      let label = 'Filtern';
      if (n === 1) label = '1 Tag filtern';
      else if (n > 1) label = `${n} Tage filtern`;
      else if (dirty) label = 'Filter entfernen'; // alles abgewählt, war aber aktiv

      this.cal.setFooter({
        applyLabel: label,
        applyDisabled: !dirty,
        resetDisabled: n === 0
      });
    }

    _syncAndSubmit() {
      this.checkboxes.forEach(cb => {
        const ts = parseInt(cb.value, 10);
        cb.checked = !isNaN(ts) && this.selectedDays.has(tsToKey(ts));
      });

      // Auf einer aktivierten Checkbox dispatchen
      const trigger = this.checkboxes.find(cb => !cb.disabled) || this.checkboxes[0];
      if (trigger) trigger.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function initDateCalendars() {
    if (!window.DpCalendar) return; // dp-calendar.js noch nicht geladen

    DATE_FILTER_KEYS.forEach(filterKey => {
      const dialog = document.getElementById('filter-dialog-' + filterKey);
      if (!dialog) return;

      if (dialog.dataset.dpcInit) {
        if (dialog._dpcInstance) dialog._dpcInstance.refresh();
        return;
      }

      dialog.dataset.dpcInit = '1';
      dialog._dpcInstance = new FacetDateCalendar(dialog, filterKey, window.DpCalendar);
    });
  }


  // ─── 4. Stadt-Filter ─────────────────────────────────────────────────────
  function initStadtFilter() {
    const dialog = document.getElementById(STADT_DIALOG_ID);
    if (!dialog || dialog.dataset.stadtInit) return;
    dialog.dataset.stadtInit = '1';

    if (!dialog.querySelector('#filter-search')) {
      const input = document.createElement('input');
      input.id = 'filter-search';
      input.type = 'text';
      input.placeholder = 'Stadt eingeben, z.B. Stuttgart';
      input.autocomplete = 'off';
      input.style.marginBottom = '1rem';
      ['blur', 'change', 'focus', 'focusout'].forEach(evt => {
        input.addEventListener(evt, e => e.stopPropagation());
      });
      input.addEventListener('input', () => filterStadtLabels(dialog, input.value));
      dialog.insertAdjacentElement('afterbegin', input);
    }

    // Event-Delegation statt Listener pro Checkbox
    dialog.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        dialog.removeAttribute('open');
      }
    });

    highlightActiveStadt(dialog);
  }

  function filterStadtLabels(dialog, value) {
    const term = value.toLowerCase().trim();
    dialog.querySelectorAll('label.facet-dialog-option').forEach(label => {
      const txt = label.textContent.toLowerCase();
      const cb  = document.getElementById(label.getAttribute('for'));
      const visible = txt.indexOf(term) === 0;
      label.style.display = visible ? '' : 'none';
      if (cb) cb.style.display = visible ? '' : 'none';
    });
  }

  function highlightActiveStadt(dialog) {
    if (!dialog) return;
    dialog.querySelectorAll('label.facet-dialog-option.active').forEach(l => l.classList.remove('active'));
    dialog.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
      const lab = dialog.querySelector(`label.facet-dialog-option[for="${cb.id}"]`);
      if (lab) lab.classList.add('active');
    });
  }


  // ─── 5. MutationObserver ─────────────────────────────────────────────────
  // Impact baut Filter & Pillen per AJAX neu auf → danach erneut anwenden.
  let _observer = null;
  let _syncScheduled = false;

  function withObserverPaused(fn) {
    if (_observer) _observer.disconnect();
    try {
      fn();
    } finally {
      if (_observer) _observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function syncAll() {
    initDateCalendars();
    initStadtFilter();
    patchActiveFacets();
    highlightActiveStadt(document.getElementById(STADT_DIALOG_ID));
  }

  function scheduleSync() {
    if (_syncScheduled) return;
    _syncScheduled = true;
    requestAnimationFrame(() => {
      _syncScheduled = false;
      withObserverPaused(syncAll);
    });
  }

  function observeDOM() {
    _observer = new MutationObserver(scheduleSync);
    _observer.observe(document.body, { childList: true, subtree: true });
  }


  // ─── 6. Ajaxinate ────────────────────────────────────────────────────────
  function initAjaxinate(attemptsLeft) {
    if (attemptsLeft === undefined) attemptsLeft = 20;
    if (typeof Ajaxinate !== 'undefined') {
      new Ajaxinate();
      return;
    }
    if (attemptsLeft > 0) {
      setTimeout(() => initAjaxinate(attemptsLeft - 1), 250);
    } else {
      console.warn('[dp] Ajaxinate wurde nicht geladen');
    }
  }


  // ─── 7. Start ────────────────────────────────────────────────────────────
  function start() {
    withObserverPaused(syncAll);
    observeDOM();
    initAjaxinate();

    // Falls dp-calendar.js erst nach dieser Datei lädt
    whenCalendar(() => withObserverPaused(initDateCalendars));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();